import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LazyImage from '../LazyImage.tsx';

interface ProjectCardProps {
  id: string;
  title: string;
  description: string;
  image: string;
  technologies: string[];
  liveUrl?: string;
  githubUrl?: string;
  category?: string;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  id,
  title,
  description,
  image,
  technologies,
  liveUrl,
  category
}) => {
  const { i18n } = useTranslation();
  const isKorean = i18n.language === 'ko';

  const handleCardClick = () => {
    // Card click goes to live URL if available
    if (liveUrl) {
      window.open(liveUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      {/* Mobile: Enhanced Card Style with larger thumbnail */}
      <div
        onClick={handleCardClick}
        className="md:hidden group flex items-stretch bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden border border-gray-100 hover:border-green-200"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleCardClick()}
        aria-label={`View ${title} project`}
      >
        {/* Preview Image - Left Side - Taller */}
        <div className="relative w-36 sm:w-40 flex-shrink-0 bg-gradient-to-br from-green-400 to-green-600 overflow-hidden">
          <LazyImage
            src={image}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-transparent" />
          {category && (
            <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-full text-xs font-medium text-green-700">
              {category}
            </div>
          )}
        </div>

        {/* Content - Right Side */}
        <div className="flex-1 p-3 sm:p-4 flex flex-col min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 group-hover:text-green-600 transition-colors line-clamp-1">
              {title}
            </h3>
            <svg
              className="w-4 h-4 flex-shrink-0 text-gray-400 group-hover:text-green-500 transition-colors"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>

          <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 mb-2 flex-1 whitespace-pre-line">
            {description}
          </p>

          <div className="flex flex-wrap gap-1 mb-3">
            {technologies.slice(0, 3).map((tech, index) => (
              <span
                key={index}
                className="px-2 py-0.5 bg-green-50 text-green-700 text-xs font-medium rounded-full"
              >
                {tech}
              </span>
            ))}
            {technologies.length > 3 && (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">
                +{technologies.length - 3}
              </span>
            )}
          </div>

          <Link
            to={`/projects/${id}`}
            onClick={(e) => e.stopPropagation()}
            className="block w-full py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-full transition-colors text-center"
          >
            {isKorean ? '자세히 보기' : 'View Details'}
          </Link>
        </div>
      </div>

      {/* Desktop: Clickable Card Style */}
      <div
        onClick={handleCardClick}
        className="hidden md:block group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 hover:border-green-200 cursor-pointer"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleCardClick()}
        aria-label={`View ${title} project`}
      >
        {/* Project Image - Taller */}
        <div className="relative h-64 overflow-hidden bg-gradient-to-br from-green-400 to-green-600">
          <LazyImage
            src={image}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
          />
          {category && (
            <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-green-700">
              {category}
            </div>
          )}
          {/* View details icon overlay */}
          <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>

        {/* Project Info */}
        <div className="p-6">
          <h3 className="text-xl font-bold text-text-primary mb-2 group-hover:text-green-600 transition-colors">
            {title}
          </h3>
          <p className="text-text-secondary text-sm mb-4 line-clamp-2 whitespace-pre-line">
            {description}
          </p>

          {/* Technologies */}
          <div className="flex flex-wrap gap-2 mb-4">
            {technologies.map((tech, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full"
              >
                {tech}
              </span>
            ))}
          </div>

          {/* Details Button - Full Width Green */}
          <Link
            to={`/projects/${id}`}
            onClick={(e) => e.stopPropagation()}
            className="block w-full py-3 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl transition-colors text-center"
          >
            {isKorean ? '자세히 보기' : 'View Details'}
          </Link>
        </div>
      </div>
    </>
  );
};

export default ProjectCard;
